import Head from 'next/head'
import { ArrowRight, Menu, X, Zap, Calendar, BookOpen, ChevronRight } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { buttonClassName } from './button'

const navItems = [
  { label: 'Services', href: '/fractional-cto', description: 'Fractional CTO & technical leadership' },
  { label: 'AI Strategy', href: '/ai-strategy', description: 'Future-proof your AI roadmap' },
  { label: 'Workshops', href: '/workshops', description: 'Hands-on team sessions' },
  { label: 'Products', href: '/product-innovation', description: 'From concept to launch' },
  { label: 'Case Studies', href: '/case-studies', description: 'Real results, real teams' },
  { label: 'Pricing', href: '/pricing', description: 'Transparent engagement models' },
]

const secondaryItems = [
  { label: 'About Juan', href: '/about' },
  { label: 'Resources', href: '/resources' },
]

type SiteHeaderProps = {
  current?: string
}

function Wordmark({ compact = false }: { compact?: boolean }) {
  return (
    <span className="group inline-flex items-center gap-2.5">
      <span className="relative flex h-8 w-8 items-center justify-center rounded-xl border border-sky-300/30 bg-sky-300/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
        <span className="absolute inset-1 rounded-lg bg-[radial-gradient(circle_at_35%_25%,rgba(125,211,252,0.35),transparent_55%)]" />
        <span className="relative font-mono text-sm font-black tracking-tight text-sky-100">Z</span>
      </span>
      <span
        className={`font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white via-sky-200 to-sky-300 ${
          compact ? 'text-2xl' : 'text-[1.7rem]'
        }`}
      >
        Zadro
      </span>
    </span>
  )
}

export function SiteHeader({ current }: SiteHeaderProps) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-slate-950/90 backdrop-blur">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <a href="/" className="transition duration-200 hover:scale-[1.02]" aria-label="Zadro home" onClick={() => setOpen(false)}>
            <Wordmark />
          </a>

          {/* Desktop nav */}
          <div className="hidden items-center gap-5 text-sm font-semibold text-slate-300 lg:flex">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`transition hover:text-sky-200 ${current === item.href ? 'text-sky-300' : ''}`}
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* Desktop CTA */}
            <a
              href="/book"
              className={buttonClassName({ className: 'hidden min-h-9 px-4 py-2 lg:inline-flex', size: 'sm' })}
            >
              Book a diagnostic
            </a>

            {/* Hamburger — mobile only */}
            <button
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-slate-700 bg-slate-900 text-slate-300 transition hover:border-sky-300/40 hover:text-sky-300 lg:hidden"
            >
              <span
                className={`absolute transition-all duration-200 ${open ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}`}
              >
                <X className="h-4.5 w-4.5" />
              </span>
              <span
                className={`absolute transition-all duration-200 ${open ? 'scale-75 opacity-0' : 'scale-100 opacity-100'}`}
              >
                <Menu className="h-4.5 w-4.5" />
              </span>
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />

        {/* Panel */}
        <div
          className={`absolute inset-x-0 top-[calc(4rem+1px)] flex flex-col bg-slate-950 border-b border-slate-800 transition-all duration-300 ease-out ${
            open ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
          }`}
          style={{ maxHeight: 'calc(100dvh - 4rem - 1px)' }}
        >
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            {/* Circuit grid accent */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/30 to-transparent" />

            {/* Main nav items */}
            <nav className="px-5 pt-6 pb-2 sm:px-8">
              <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                Navigation
              </p>
              <ul className="space-y-1">
                {navItems.map((item) => (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      onClick={() => setOpen(false)}
                      className={`group flex items-center justify-between rounded-xl px-4 py-3.5 transition-colors hover:bg-slate-900 ${
                        current === item.href
                          ? 'bg-sky-300/10 text-sky-300'
                          : 'text-slate-200'
                      }`}
                    >
                      <div>
                        <span className="block font-semibold leading-tight">{item.label}</span>
                        <span className="mt-0.5 block text-xs text-slate-500 group-hover:text-slate-400">
                          {item.description}
                        </span>
                      </div>
                      <ChevronRight
                        className={`h-4 w-4 flex-none transition-transform group-hover:translate-x-0.5 ${
                          current === item.href ? 'text-sky-300' : 'text-slate-600'
                        }`}
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Secondary links */}
            <div className="mx-5 mt-4 border-t border-slate-800 pt-4 sm:mx-8">
              <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                More
              </p>
              <div className="flex gap-2">
                {secondaryItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className="rounded-lg border border-slate-800 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-400 transition hover:border-slate-700 hover:text-slate-200"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Sticky toolbar */}
          <div className="border-t border-slate-800 bg-slate-950 px-5 py-4 sm:px-8">
            <div className="flex items-center gap-3">
              <a
                href="/book"
                onClick={() => setOpen(false)}
                className={buttonClassName({ size: 'lg', className: 'flex-1 justify-center' })}
              >
                <Calendar className="h-4 w-4" />
                Book 18-min diagnostic
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/pricing"
                onClick={() => setOpen(false)}
                className={buttonClassName({ size: 'lg', variant: 'outline', className: 'min-h-11 px-4' })}
                aria-label="Pricing"
              >
                <Zap className="h-4 w-4" />
              </a>
              <a
                href="/resources"
                onClick={() => setOpen(false)}
                className={buttonClassName({ size: 'lg', variant: 'outline', className: 'min-h-11 px-4' })}
                aria-label="Resources"
              >
                <BookOpen className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

type SiteLayoutProps = {
  children: React.ReactNode
  current?: string
  description: string
  title: string
}

export function SiteLayout({ children, current, description, title }: SiteLayoutProps) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Head>
      <SiteHeader current={current} />
      {children}
      <SiteFooter />
    </div>
  )
}

export function SiteFooter() {
  return (
    <footer className="border-t border-slate-800 px-5 py-10 text-sm text-slate-400 sm:px-8">
      <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-[1fr_1.4fr_auto] md:items-start">
        <div>
          <a href="/" className="inline-flex transition duration-200 hover:scale-[1.02]" aria-label="Zadro home">
            <Wordmark compact />
          </a>
          <p className="mt-4 max-w-sm leading-6">
            Fractional CTO, AI strategy, workshops, and product innovation for teams that need better
            technical wiring.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="transition hover:text-sky-200">
              {item.label}
            </a>
          ))}
          <a href="/about" className="transition hover:text-sky-200">
            About Juan
          </a>
          <a href="/resources" className="transition hover:text-sky-200">
            Resources
          </a>
          <a href="/book" className="transition hover:text-sky-200">
            Book
          </a>
        </div>
        <a
          href="/book"
          className={buttonClassName({ variant: 'outline' })}
        >
          Start with 18 minutes
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </footer>
  )
}
